import {
  PreSalesReportPeriodType,
  SalesLiftResponseVariable,
  LandingPageChoices,
  WeeklyMetricsGroups,
  WeeklyMetricsName,
  CampaignBenchmarkResultType,
  CampaignBenchmarkMethod,
  CampaignBenchmarkMethodNeedToTransf,
  CampaignBenchmarkDataSource,
  CampaignServiceChoices,
  SelectOption,
} from './types';

export const campaignConfidenceOptions = [
  {label: '1 - Proactive Proposal', value: 1},
  {label: '2 - Qualified Proposal', value: 2},
  {label: '3 - Qualified Opportunity', value: 3},
  {label: '4 - Likely to Accept', value: 4},
  {label: '5 - Accepted', value: 5},
  {label: '0', value: 0},
];

export const campaignStoreIdModeOptions = [
  {label: 'Tdlinx Store Code', value: 'tdlinx_store_code'},
  {label: 'Store number and retailer name combination', value: 'store_number_retailer_name'},
];

export const campaignActionPriorityOptions = [
  {label: 'High', value: 'High'},
  {label: 'Low', value: 'Low'},
];

export const campaignDataGranularityOptionRegional = {label: 'Regional Level', value: 'regional_level'};
export const campaignDataGranularityOptionStore = {label: 'Store Level', value: 'store_level'};
export const campaignDataGranularityOptionNoDefault = {label: 'No Default (use retailer setting)', value: 'no_default'};

export const campaignDataGranularityOptions = [
  campaignDataGranularityOptionRegional,
  campaignDataGranularityOptionStore,
];

export const campaignDataGranularityOptionsWithNoDefault = [
  campaignDataGranularityOptionNoDefault,
  campaignDataGranularityOptionRegional,
  campaignDataGranularityOptionStore,
];

export const responseVariableOptions: {label: string; value: SalesLiftResponseVariable}[] = [
  {label: 'Sales Dollars', value: 'sales_dollars'},
  {label: 'Sales Units', value: 'sales_units'},
  {label: 'EQ', value: 'eq'},
];

export const salesLiftItemGroupOptions = [
  {label: 'halo', value: 'halo'},
  {label: 'hero', value: 'hero'},
  {label: 'combined', value: 'combined'},
];

export const numPostCampaignWeeksOptions = [0, 1, 2, 3, 4].map(v => {
  return {
    value: v,
    label: `${v}`,
  };
});

export const weekDayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const testControlEligibilityOptions = [
  {value: 'not restricted'},
  {value: 'include test'},
  {value: 'include control'},
  {value: 'exclude test'},
  {value: 'exclude control'},
  {value: 'exclude'},
].map(it => ({...it, label: it.value}));

export const taskCodeMap = {
  DATA_LOAD_FILE_PREPARE: {
    title: 'Preparing for data loading',
    link: '/data-loading',
  },
  DATA_LOAD_FILE_PROCESS: {
    title: 'Processing files',
    link: '/data-loading',
  },
  calc_sales_metrics: {
    title: 'Calculating sales metrics',
    link: '/launch/analysis',
  },
  calc_sales_weekly_metrics: {
    title: 'Calculating sales weekly metrics',
    link: '/launch/analysis',
  },
  calc_upc_metrics: {
    title: 'Calculating UPC metrics',
    link: '/launch/analysis',
  },
  ttest: {
    title: 'TTest',
    link: '/launch/analysis',
  },
  calc_store_metrics: {
    title: 'Calculating store metrics',
    link: '/launch/analysis',
  },
  per_store_sales_calculations: {
    title: 'Calculating per store sales metrics',
    link: '/launch/analysis',
  },
  per_item_sales_calculations: {
    title: 'Calculating per item sales metrics',
    link: '/launch/analysis',
  },
  calculate_campaign_weekly_item_metrics: {
    title: 'Calculating weekly item metrics',
    link: '/configure/items',
  },
  generate_item_insight_flags: {
    title: 'Refreshing item insights',
    link: '/configure/items',
  },
  per_store_sales_rollup: {
    title: 'Calculating per store Rollups',
    link: '/launch/analysis',
  },
  test_control: {
    title: 'Computing test/control',
    link: '/launch/analysis',
  },
  generate_valassis_config_files: {
    title: 'Generating Agg Report',
    link: '/launch/analysis',
  },
  sync_campaign_entities_from_sales: {
    title: 'Sync Campaign Entities from Sales Dataset',
    link: '/launch/analysis',
  },
  calculate_sales_report_data: {
    title: 'Calculating sales report data',
    link: '/dashboard/sales_report',
  },
  calculate_sales_report_data_regional: {
    title: 'Calculating sales report data (regional)',
    link: '/dashboard/sales_report',
  },
  push_to_hubspot_task_create: {
    title: 'Creating campaign deal on HubSpot',
    link: '/launch/hubspot',
  },
  push_to_hubspot_task_update: {
    title: 'Updating campaign deal on HubSpot',
    link: '/launch/hubspot',
  },
  check_hubspot_properties_task: {
    title: 'Checking HubSpot properties for discrepancies',
    link: '/launch/hubspot',
  },
  sales_lift_key_findings: {
    title: 'Generating AI key findings',
    link: '/launch/analysis',
  },
  multivariate_analysis: {
    title: 'Running multivariate creative analysis',
    link: '/launch/analysis',
  },
};

export const preSalesReportPeriodTypeLabelMap: Record<PreSalesReportPeriodType, string> = {
  [PreSalesReportPeriodType.latest_4_wks]: 'Latest 4 Weeks',
  [PreSalesReportPeriodType.latest_13_wks]: 'Latest 13 Weeks',
  [PreSalesReportPeriodType.latest_52_wks]: 'Latest 52 Weeks',
};

export const preCampaignTypeOptions = ['All', '52-week', 'previous-year'];
export const testControlMethodologyOptions = ['All', 'client-provided', 'pathformance'];
export const postWeekOptions = ['All', '0', '1', '2'];
export const a4PaperSize = {in: {width: 8.27, height: 11.69}};
export const pathFormanceBranding = {
  colors: {
    teal: '#00D6D0',
    blue: '#243E76',
  },
};

// Filter range constants for multi-campaign dashboard
export const filterRangeConstants = {
  budget: {
    min: 0,
    max: 500000,
    step: 10000,
  },
  salesVolume: {
    min: 0,
    max: 1000000,
    step: 50000,
  },
  impressions: {
    min: 0,
    max: 10000000,
    step: 100000,
  },
  itemCount: {
    min: 0,
    max: 100,
    step: 5,
  },
};

// Campaign Benchmark Data select option arrays
export const campaignBenchmarkResultTypeOptions = Object.values(CampaignBenchmarkResultType).map(v => ({
  label: v
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' '),
  value: v,
}));
export const campaignBenchmarkMethodOptions = Object.values(CampaignBenchmarkMethod).map(v => ({
  label: v,
  value: v,
}));
export const campaignBenchmarkMethodNeedToTransfOptions = Object.values(CampaignBenchmarkMethodNeedToTransf).map(v => ({
  label: v,
  value: v,
}));
export const campaignBenchmarkDataSourceOptions = Object.values(CampaignBenchmarkDataSource).map(v => ({
  label: v,
  value: v,
}));
export const campaignServiceOptions = Object.values(CampaignServiceChoices).map(v => ({
  label: v,
  value: v,
}));

export const landingPageURLs = {
  [LandingPageChoices.DEFAULT]: '/home',
  [LandingPageChoices.LANDING_PAGE]: 'https://marketing.pathformance.com/pathx-landing-1',
};

export const metricNamesByGroup = {
  [WeeklyMetricsGroups.upc_weekly_average_sales]: [
    WeeklyMetricsName.upc_weekly_average_sales_per_store,
    WeeklyMetricsName.upc_weekly_average_sales_per_test_store,
    WeeklyMetricsName.upc_weekly_average_sales_per_control_store,
  ],
  [WeeklyMetricsGroups.upc_weekly_total_sales]: [
    WeeklyMetricsName.upc_weekly_total_sales_all_stores,
    WeeklyMetricsName.upc_weekly_total_sales_test_stores,
    WeeklyMetricsName.upc_weekly_total_sales_control_stores,
  ],
  [WeeklyMetricsGroups.upc_weekly_num_stores_selling]: [
    WeeklyMetricsName.upc_weekly_num_stores_selling,
    WeeklyMetricsName.upc_weekly_num_test_stores_selling,
    WeeklyMetricsName.upc_weekly_num_control_stores_selling,
  ],
  [WeeklyMetricsGroups.area_weekly_average_sales]: [
    WeeklyMetricsName.area_weekly_average_sales_per_store_hero,
    WeeklyMetricsName.area_weekly_average_sales_per_store_halo,
    WeeklyMetricsName.area_weekly_average_sales_per_store_total,
  ],
  [WeeklyMetricsGroups.area_weekly_total_sales]: [
    WeeklyMetricsName.area_weekly_total_sales_hero,
    WeeklyMetricsName.area_weekly_total_sales_halo,
    WeeklyMetricsName.area_weekly_total_sales_total,
  ],
  [WeeklyMetricsGroups.area_weekly_num_stores_selling]: [
    WeeklyMetricsName.area_weekly_num_stores_selling_hero,
    WeeklyMetricsName.area_weekly_num_stores_selling_halo,
    WeeklyMetricsName.area_weekly_num_stores_selling_total,
  ],
};

export const MAP_EXPORT_WIDTH_PX = 1520;
export const MAP_EXPORT_SCALE = 2.0;

export const BLANK_OPTION_LABEL = 'Blank / null';

export const analysisSystemVariables: {
  store: SelectOption<string>[];
  regional: SelectOption<string>[];
  item: SelectOption<string>[];
} = {
  store: [
    {value: 'dma', label: 'DMA'},
    {value: 'zip_code', label: 'Zip Code'},
    {value: 'tc_geo_code', label: 'Zip3'},
  ] as const,
  regional: [] as const,
  item: [
    {value: 'clean_item_brand', label: 'Clean Item Brand'},
    {value: 'clean_item_category', label: 'Clean Item Category'},
  ] as const,
} as const;
export const analysisOtherVariableFilters = {
  store: analysisSystemVariables.store.reduce((o, v) => {
    o[v.value] = [];
    return o;
  }, {}),
  regional: analysisSystemVariables.regional.reduce((o, v) => {
    o[v.value] = [];
    return o;
  }, {}),
  item: analysisSystemVariables.item.reduce((o, v) => {
    o[v.value] = [];
    return o;
  }, {}),
} as const;
